/**
 * useRKHLocation Hook
 * Custom hook for managing RKH location/block selection and registration
 */

import { useState, useCallback } from 'react';
import rkhApiService from '../services/rkhApiService';
import {
    RKH,
    AvailableBlock,
    LocationInput,
    LocationRegistrationRequest,
} from '../types';

interface UseRKHLocationReturn {
    availableBlocks: AvailableBlock[];
    rkhInfo: RKH | null;
    loading: boolean;
    error: string | null;
    submitting: boolean;
    fetchAvailableBlocks: (rkhId: string) => Promise<void>;
    registerLocations: (rkhId: string, locations: LocationInput[], userEmail: string) => Promise<boolean>;
    clearData: () => void;
}

export const useRKHLocation = (): UseRKHLocationReturn => {
    const [availableBlocks, setAvailableBlocks] = useState<AvailableBlock[]>([]);
    const [rkhInfo, setRkhInfo] = useState<RKH | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const fetchAvailableBlocks = useCallback(async (rkhId: string) => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await rkhApiService.getAvailableBlocks(rkhId);
            
            if (response.success && response.data) {
                setAvailableBlocks(response.data);
                setRkhInfo(response.rkh ?? null);
            } else {
                throw new Error('Failed to fetch available blocks');
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || 
                                err.message || 
                                'Gagal memuat data blok tersedia';
            setError(errorMessage);
            setAvailableBlocks([]);
            setRkhInfo(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const registerLocations = useCallback(async (
        rkhId: string,
        locations: LocationInput[],
        userEmail: string
    ): Promise<boolean> => {
        setSubmitting(true);
        setError(null);
        
        try {
            const request: LocationRegistrationRequest = {
                create_by: userEmail,
                write_by: userEmail,
                locations,
            };

            const response = await rkhApiService.registerLocations(rkhId, request);
            
            if (response.success) {
                return true;
            } else {
                throw new Error(response.error || 'Failed to register locations');
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || 
                                err.response?.data?.message || 
                                err.message || 
                                'Gagal menyimpan lokasi';
            setError(errorMessage);
            return false;
        } finally {
            setSubmitting(false);
        }
    }, []);

    const clearData = useCallback(() => {
        setAvailableBlocks([]);
        setRkhInfo(null);
        setError(null);
        setLoading(false);
        setSubmitting(false);
    }, []);

    return {
        availableBlocks,
        rkhInfo,
        loading,
        error,
        submitting,
        fetchAvailableBlocks,
        registerLocations,
        clearData,
    };
};
